import { FieldInfo, FunctionDependency, Node, PhaseInfo } from "../types";
import { isMongoId } from "./findNode";

// Helper functions

/**
 *
 * @param nodes - Array of Node objects
 * @param processId - Process ID in string format
 * @throws Error - if process is not found
 * @returns Node - Node that has the same id as process
 */
const getProcess = (nodes: Node[], processId: string) => {
  const val = nodes.find((node: Node) => node.id === processId);
  if (val == undefined) {
    throw new Error("No process found!");
  }
  return val;
};

const getFieldName = (id: string, processFields: FieldInfo[]) =>
  processFields.find((field) => field.id === id)?.name ?? isMongoId(id);

const getField = (id: string, processFields: FieldInfo[]) => {
  return processFields.find((field) => field.id === id);
};
const getPhase = (id: string, processPhases: PhaseInfo[]) =>
  processPhases.find((phase) => phase.id === id);

/**
 * Generates  human readable labels for dependencies
 * TODO: better documentation aka comments and unit tests
 *
 * @author Patrik Larsen <patrik.larsen@kvanttori.fi>
 */
const getDependencyLabel = (
  dependency: FunctionDependency,
  currentNode: Node,
  nodes: Node[]
) => {
  const getSameDependencyLabel = () => {
    // Same process dependency
    const data = dependency.data[dependency.data.length - 1] ?? ""; // for "=" dependencies the last index is the value
    return `Field: ${getFieldName(data, currentNode.fields)}`;
  };

  const getLinkedToFieldLabel = () => {
    // Linked to Field
    const [linkedFieldId, fieldId] = dependency.data;

    // Going through processes and finding the field we're after
    const processIds = getField(linkedFieldId, currentNode.fields)?.data;
    if (processIds) {
      for (const processId of processIds) {
        try {
          const process = getProcess(nodes, processId);
          if (getField(fieldId, process.fields)) {
            const label = getFieldName(fieldId, process.fields);
            return `Linked to Field: ${label} from ${
              process.name
            } in ${getFieldName(linkedFieldId, currentNode.fields)}`;
          }
        } catch (error) {
          console.log("Failing process ID: ", processId);
        }
      }
    }
    return `Couldn't find dependency`;
  };

  const getLinkedFromFieldLabel = () => {
    // Linked from field
    const [processId, fieldId] = dependency.data;

    try {
      const process = getProcess(nodes, processId);
      const label = getFieldName(fieldId, process.fields);

      return `Linked from field: ${label} of ${process.name}`;
    } catch (error) {
      console.log("Failing process ID: ", processId);
    }
    return `Couldn't find dependency`;
  };

  const getMiscLablel = () => {
    // Linked phase or process
    const [processId, phaseId] = dependency.data;

    try {
      const process = getProcess(nodes, processId);
      if (process) {
        if (phaseId) {
          return `Phase Id: ${getPhase(phaseId, process.phases)?.name} of ${
            process.name
          }`;
        } else {
          return `Process Id:  ${process.name}`;
        }
      } else {
        return `Unknown process`;
      }
    } catch (error) {
      console.log("Failing process ID: ", processId);
    }
    return `Unknown process`;
  };

  switch (dependency.type) {
    case "=":
      return getSameDependencyLabel();
    case ">":
      return getLinkedToFieldLabel();
    case "<":
      return getLinkedFromFieldLabel();
    default:
      //default type is ?
      return getMiscLablel();
  }
};

export default getDependencyLabel;
